/** leads/NewCorpPage.tsx — 영업 DB 「신규법인 리스트」 (2026-10-17 지도사 「영업DB에 넣을거야 · 탭 이름은 신규법인 리스트
 *  · 로움에도 벤처인증 권유 DM 넣을거야 · 배정도 할거고 · 벤처도 API로 조회하면 현재 있는지 아닌지 알 수 있잖아」)
 *  칸: 회사명 · 업종 · 설립 · 대표자 · 사업자번호 · 법인등록번호 · 연매출 · 영업이익 · 자본금 · 지역 · 벤처 · 유효기간 · 배정
 *  · 벤처 여부: 중소벤처24 확인 결과가 먼저(「확인」 표시), 확인 전이면 벤처기업 명단 기준 — 「벤처 여부 확인」 은 체크한 회사만 30건씩
 *  · DM: 벤처확인을 받으면 설립 출자금이 벤처투자 소득공제 대상 (창업감면은 넣지 않음 — 창업기업 여부를 알 수 없어서)
 *  · 배정: 리드(venture_new)로 올린 뒤 만료 DB 와 같은 배정 창(LeadAssignModal)
 *  모양은 같은 탭 묶음의 인증 만료 화면(CertExpiryPage)과 같은 ce-* 틀. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { newCorpList, newCorpMeta, newCorpPromote, newCorpVentureCheck, newCorpUpload, unassignLead, getMeta } from './api';
import type { NewCorpRow, NewCorpMeta } from './api';
import type { LeadMeta } from './types';
import LeadAssignModal from './LeadAssignModal';

interface Props { onToast: (m: string, t?: string) => void }

const VENTURE_OPTS: [string, string][] = [['', '벤처 전체'], ['none', '벤처 아님 (권유 대상)'], ['valid', '벤처기업'], ['past', '벤처 만료'], ['unchecked', '확인 전']];
const CHUNK = 30;

/** 원 → 억 (음수 · 소수 1자리) */
function eok(v: number | null): string {
  if (v == null) return '-';
  const e = v / 1e8;
  return (Math.abs(e) >= 100 ? e.toFixed(0) : e.toFixed(1)) + '억';
}
/** 원 → 만원 (자본금은 수천만 단위가 많다) */
function manOrEok(v: number | null): string {
  if (v == null) return '-';
  return Math.abs(v) >= 1e8 ? eok(v) : Math.round(v / 1e4).toLocaleString() + '만';
}

function FsCell({ r, v, man }: { r: NewCorpRow; v: number | null; man?: boolean }) {
  if (r.fsResult !== 'hit') {
    return <span className="nc-mut" title={r.fsResult === 'none' ? '공시 재무정보가 없는 법인입니다 (외부감사 대상 아님 가능)' : '재무는 밤마다 조금씩 채웁니다'}>{r.fsResult === 'none' ? '없음' : '-'}</span>;
  }
  return <span className={'nc-num' + (v != null && v < 0 ? ' neg' : '')} title={(r.fsYear || '') + '년 결산'}>{man ? manOrEok(v) : eok(v)}</span>;
}

function VentureCell({ r }: { r: NewCorpRow }) {
  const label = r.ventureState === 'valid' ? '벤처' : r.ventureState === 'past' ? '만료' : '아님';
  const src = r.ventureChecked ? '중소벤처24 확인' + (r.ventureCheckedAt ? ' · ' + String(r.ventureCheckedAt).slice(0, 10) : '') : '벤처기업 명단 기준 (확인 전)';
  return (
    <span className={'nc-v ' + r.ventureState} title={src}>
      {label}{r.ventureChecked ? <i className="nc-ck">확인</i> : null}
    </span>
  );
}

/** 관리자 DM 새 창 — 인증 헤더가 필요해 fetch 후 문서로 쓴다 · 창은 클릭 시점에 먼저 연다(팝업 차단 방지) */
async function openDm(url: string): Promise<void> {
  const w = window.open('', '_blank');
  if (!w) throw new Error('팝업이 차단되었습니다 — 브라우저 팝업 허용 후 다시 시도하세요');
  w.document.write('<p style="font-family:sans-serif;padding:30px;color:#6B7280">DM 안내장 만드는 중…</p>');
  try {
    const res = await fetch(url, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });
    if (!res.ok) { const j = await res.json().catch(() => null); throw new Error((j && (j as { error?: string }).error) || 'DM 생성 실패'); }
    const html = await res.text();
    w.document.open(); w.document.write(html); w.document.close();
  } catch (e) { w.close(); throw e; }
}

export default function NewCorpPage({ onToast }: Props) {
  const [rows, setRows] = useState<NewCorpRow[]>([]);
  const [meta, setMeta] = useState<NewCorpMeta | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [year, setYear] = useState(0);
  const [venture, setVenture] = useState('');
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [region, setRegion] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [onlyAssigned, setOnlyAssigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checking, setChecking] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [assignIds, setAssignIds] = useState<string[] | null>(null);
  const [leadMeta, setLeadMeta] = useState<LeadMeta | null>(null);
  const [promoting, setPromoting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // 한 번에 보기 100 · 500 · 1000개 (2026-10-17 지도사) — 고른 값은 이 브라우저에 기억
  const [pageSize, setPageSize] = useState<number>(() => { try { const v = Number(localStorage.getItem('nc_page_size')); return [100, 500, 1000].includes(v) ? v : 100; } catch { return 100; } });
  const pickSize = (n: number) => { setPageSize(n); setPage(1); try { localStorage.setItem('nc_page_size', String(n)); } catch { /* 기억 못 해도 동작 */ } };

  useEffect(() => { getMeta().then(setLeadMeta).catch(() => setLeadMeta(null)); }, []);
  const loadMeta = useCallback(() => { newCorpMeta().then(setMeta).catch(() => setMeta(null)); }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await newCorpList({ page, pageSize, q: qApplied || undefined, region: region || undefined, sigungu: sigungu || undefined, year: year || undefined, venture: venture || undefined, assigned: onlyAssigned ? 'yes' : undefined });
      setRows(r.rows); setTotal(r.total);
    } catch (e) { onToast((e as Error).message, 'error'); }
    setLoading(false);
  }, [page, pageSize, qApplied, region, sigungu, year, venture, onlyAssigned, onToast]);
  useEffect(() => { load(); }, [load]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const selectable = rows.filter((r) => r.bizNo);
  const allChecked = selectable.length > 0 && selectable.every((r) => sel[r.id]);
  const toggleAll = (on: boolean) => setSel(() => { const n: Record<string, boolean> = {}; if (on) selectable.forEach((r) => { n[r.id] = true; }); return n; });

  const onFile = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    onToast('명단을 올리는 중… (12만 행 기준 수십 초)');
    try {
      const r = await newCorpUpload(f);
      onToast(`반영 완료 — 새로 ${r.inserted.toLocaleString()} · 갱신 ${r.updated.toLocaleString()}` + (r.errorCount ? ` · 건너뜀 ${r.errorCount.toLocaleString()}행` : ''));
      setPage(1); load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const doPromote = async (ids: string[]) => {
    setPromoting(true);
    try {
      const r = await newCorpPromote(ids);
      if (r.failed.length) onToast(`배정 준비 실패 ${r.failed.length}건 — ${r.failed[0].corp_name}: ${r.failed[0].reason}`, r.leadIds.length ? undefined : 'error');
      if (r.leadIds.length) setAssignIds(r.leadIds);
      setSel({});
    } catch (e) { onToast((e as Error).message, 'error'); }
    setPromoting(false);
  };

  /** 체크한 회사만 30건씩 중소벤처24 확인 */
  const doCheck = async () => {
    if (!selIds.length) { onToast('벤처 여부를 확인할 회사를 왼쪽 칸에서 체크해 주세요', 'error'); return; }
    const sum = { valid: 0, none: 0, error: 0 };
    try {
      for (let i = 0; i < selIds.length; i += CHUNK) {
        setChecking(`확인 중 ${Math.min(i + CHUNK, selIds.length).toLocaleString()}/${selIds.length.toLocaleString()}`);
        const r = await newCorpVentureCheck(selIds.slice(i, i + CHUNK));
        sum.valid += r.valid; sum.none += r.none; sum.error += r.error;
      }
      onToast(`벤처 여부 확인 — 벤처 ${sum.valid} · 아님 ${sum.none}` + (sum.error ? ` · 확인 안 됨 ${sum.error}(명단 기준 유지)` : ''));
      load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); }
    setChecking('');
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const empty = !loading && meta != null && meta.total === 0;
  const yearChips: { y: number; l: string }[] = [{ y: 0, l: '전체' }].concat((meta?.years || []).slice().sort((a, b) => a - b).map((y) => ({ y, l: y + '년 설립' })));

  return (
    <div>
      <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => onFile(e.target.files ? e.target.files[0] : null)} />
      {empty ? (
        <div className="ce-empty">
          <div className="big">📥</div>
          <div className="t1">신설법인 명단을 올려 주세요</div>
          <div className="t2">CSV (회사명 · 대표자명 · 사업자번호 · 법인등록번호 · 시도 · 시군구 · 설립연도 · 주소 · 업종 · 기업유형 · 전화번호)<br />다시 올리면 같은 회사는 한 줄로 갱신됩니다.</div>
          <button className="ce-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? '올리는 중…' : '명단 CSV 올리기'}</button>
        </div>
      ) : (<>
        <div className="ce-chips">
          {yearChips.map((c) => (
            <button key={c.y} className={'ce-chip' + (year === c.y ? ' on' : '')} onClick={() => { setYear(c.y); setPage(1); }}>{c.l}</button>
          ))}
          <span className="ce-total">
            {loading ? '조회 중…' : `${total.toLocaleString()}개사`}
            {meta ? <span className="nc-sum"> · 전체 {meta.total.toLocaleString()} · 벤처 아님 {meta.none.toLocaleString()} · 확인 전 {meta.unchecked.toLocaleString()}</span> : null}
          </span>
        </div>
        <div className="ce-bar">
          <input className="ce-inp" placeholder="회사명 · 대표자 · 사업자번호" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setQApplied(q.trim()); setPage(1); } }} onBlur={() => { if (q.trim() !== qApplied) { setQApplied(q.trim()); setPage(1); } }} />
          <select className="ce-sel" value={region} onChange={(e) => { setRegion(e.target.value); setSigungu(''); setPage(1); }}>
            <option value="">지역 전체</option>
            {(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="ce-sel" value={sigungu} disabled={!region} onChange={(e) => { setSigungu(e.target.value); setPage(1); }}>
            <option value="">{region ? '시군구 전체' : '시군구 (지역 먼저)'}</option>
            {(meta?.sigungus || []).filter((s) => s.region === region).map((s) => <option key={s.sigungu} value={s.sigungu}>{s.sigungu}</option>)}
          </select>
          <select className="ce-sel" value={venture} onChange={(e) => { setVenture(e.target.value); setPage(1); }}>
            {VENTURE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="nc-lab"><input type="checkbox" checked={onlyAssigned} onChange={(e) => { setOnlyAssigned(e.target.checked); setPage(1); }} /> 배정만</label>
          <button className="ce-btn" style={{ background: '#7C3AED' }} disabled={!!checking} title="체크한 회사가 지금 벤처기업인지 중소벤처24 로 확인합니다" onClick={doCheck}>{checking || '벤처 여부 확인'}</button>
          <button className="ce-btn" disabled={promoting || selIds.length === 0} onClick={() => doPromote(selIds)}>{promoting ? '준비 중…' : `선택 ${selIds.length}건 배정`}</button>
        </div>
        <div className="nc-wrap">
          <table className="ce-tbl nc-tbl">
            <colgroup>
              <col style={{ width: 40 }} /><col style={{ width: 190 }} /><col style={{ width: 140 }} /><col style={{ width: 56 }} /><col style={{ width: 80 }} /><col style={{ width: 118 }} /><col style={{ width: 138 }} />
              <col style={{ width: 66 }} /><col style={{ width: 70 }} /><col style={{ width: 70 }} /><col style={{ width: 260 }} /><col style={{ width: 86 }} /><col style={{ width: 168 }} /><col style={{ width: 110 }} /><col style={{ width: 124 }} />
            </colgroup>
            <thead><tr>
              <th><input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} title="이 페이지 전체 선택" /></th>
              <th>회사명</th><th>업종</th><th>설립</th><th>대표자</th><th>사업자번호</th><th>법인등록번호</th><th className="r">연매출</th><th className="r">영업이익</th><th className="r">자본금</th><th>주소</th><th>벤처</th><th>벤처 유효기간</th><th>배정</th><th />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><input type="checkbox" checked={!!sel[r.id]} disabled={!r.bizNo} title={r.bizNo ? '' : '사업자번호가 없는 회사입니다'} onChange={() => setSel((p) => ({ ...p, [r.id]: !p[r.id] }))} /></td>
                  <td className="nm nc-ell" title={r.corpName + (r.address ? '\n' + r.address : '')}>{r.corpName}</td>
                  <td className="mut nc-ell" title={r.industry}>{r.industry || '-'}</td>
                  <td className="mut nc-num">{r.estbYear || '-'}</td>
                  <td className="mut nc-ell" title={r.ceoName}>{r.ceoName || '-'}</td>
                  <td className="mut nc-num">{r.bizNo ? r.bizNo.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3') : <span style={{ color: '#B45309' }}>없음</span>}</td>
                  <td className="mut nc-num">{r.corpNo ? r.corpNo.replace(/^(\d{6})(\d{7})$/, '$1-$2') : '-'}</td>
                  <td className="r"><FsCell r={r} v={r.fsSale} /></td>
                  <td className="r"><FsCell r={r} v={r.fsOp} /></td>
                  <td className="r"><FsCell r={r} v={r.fsCapital} man /></td>
                  <td className="mut nc-ell" title={r.address}>{r.address || (r.region + (r.sigungu ? ' ' + r.sigungu : '')) || '-'}</td>{/* 주소 전체 (2026-10-17 지도사 「지역만이 아니라 구체적인 주소를 다」) */}
                  <td><VentureCell r={r} /></td>
                  <td className="mut nc-num nc-ell">{r.ventureFrom || r.ventureTo ? (r.ventureFrom || '') + ' ~ ' + (r.ventureTo || '') : '-'}</td>
                  <td className="nc-ell">
                    {r.assigned && r.fpName ? (<>
                      <b style={{ fontSize: 12.5 }}>{r.fpName}</b>
                      <button className="ce-btn sub nc-mini" title="한 번 누르면 회수 — 미배정으로"
                        onClick={() => unassignLead(r.leadId as string).then(() => { onToast((r.fpName || '') + ' 배정 회수 완료'); load(); }).catch((e) => onToast((e as Error).message, 'error'))}>회수</button>
                    </>) : <span className="mut">-</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="ce-btn sub nc-mini" title="벤처확인 · 소득공제 안내 DM (인쇄)" onClick={() => openDm('/api/new-corps/rows/' + r.id + '/dm').catch((e) => onToast((e as Error).message, 'error'))}>DM</button>
                    <button className="ce-btn nc-mini" disabled={promoting || !r.bizNo} title={r.bizNo ? '' : '사업자번호가 없는 회사입니다'} onClick={() => doPromote([r.id])}>배정</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><td colSpan={15} className="ce-none">조건에 맞는 회사가 없습니다</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="ce-pg" style={{ justifyContent: pages > 1 ? 'center' : 'flex-start', position: 'relative' }}>
          <button disabled={uploading} onClick={() => fileRef.current?.click()} title="신설법인 명단 CSV 올리기 (같은 회사는 갱신)"
            style={{ position: 'absolute', left: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C0C4CC', textDecoration: 'underline dotted', opacity: uploading ? 1 : 0.7 }}>
            {uploading ? '올리는 중…' : '명단 CSV 올리기'}</button>
          {pages > 1 && (<>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>이전</button>
            <span>{page.toLocaleString()} / {pages.toLocaleString()}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>다음</button>
          </>)}
          <select className="ce-sel nc-size" value={pageSize} onChange={(e) => pickSize(Number(e.target.value))} aria-label="한 번에 보기">
            {[100, 500, 1000].map((n) => <option key={n} value={n}>{n.toLocaleString()}개씩</option>)}
          </select>
        </div>
      </>)}
      {assignIds && <LeadAssignModal leadIds={assignIds} meta={leadMeta}
        onClose={() => setAssignIds(null)}
        onDone={() => { setAssignIds(null); load(); onToast('배정 완료 — 리드 포털에 벤처확인 기한과 함께 보입니다'); }}
        onToast={onToast} />}
    </div>
  );
}
